import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useGetLearningModulesQuery, type LearningModule as ApiLearningModule } from '../store/api/learningApi';
import { useGetMyGamificationOverviewQuery } from '../store/api/gamificationApi';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { streakMessage } from '../utils/streakMessage';

// Display type for module cards
interface DisplayModule {
  id: string;
  title: string;
  description: string;
  lessons: number;
  /** Lessons with content; progress counts only these. */
  availableLessons: number;
  duration: string;
  progress: number;
  slug: string;
  category: string;
}

export function mapApiModule(m: ApiLearningModule): DisplayModule {
  const completed = m.completedLessons ?? 0;
  // Lessons without content are not part of progress (they cannot be completed).
  const total = m.availableLessons ?? m.lessons;
  return {
    id: m.id,
    title: m.title,
    description: m.description,
    lessons: m.lessons,
    availableLessons: total,
    duration: `${m.durationMinutes} min`,
    progress: total > 0 ? Math.round((completed / total) * 100) : 0,
    slug: m.slug,
    category: m.category,
  };
}

export function LearnPage() {
  const [selectedCategory, setSelectedCategory] = useState('foundations');

  // One request: the full list carries every module with its category, so the
  // active tab is a client-side filter (a second per-category query used to
  // repeat /learn/modules on every view and every tab switch).
  const { data: allData, isLoading, isError, error, refetch } = useGetLearningModulesQuery();
  // Real current streak (practice + completed conversations), never a made-up number
  const { data: gamification } = useGetMyGamificationOverviewQuery();
  const currentStreak = gamification?.stats.currentStreak;

  const categories = allData?.categories ?? [];
  // The default tab may not exist in this catalog: fall back to the first
  // category rather than showing an empty grid with no tab selected.
  const activeCategory = categories.some((c) => c.id === selectedCategory)
    ? selectedCategory
    : categories[0]?.id ?? selectedCategory;
  const setActiveCategory = setSelectedCategory;

  // Team challenges (P3.5) are the workspace's shared goals: offered here only
  // while the server has them on and Central grants training:read (the route's guard).
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const { hasCapability } = useCentralConsumer();
  const showTeamChallenges = serverCaps?.teamChallenges === true && hasCapability('training', 'read');
  const modules = useMemo(
    () => (allData?.modules ?? []).filter((m) => m.category === activeCategory).map(mapApiModule),
    [allData, activeCategory]
  );

  // Overall progress from ALL modules (not just active category)
  // Modules with no available lesson are not part of progress.
  const allModules = useMemo(
    () => (allData?.modules?.map(mapApiModule) ?? []).filter((m) => m.availableLessons > 0),
    [allData]
  );
  const totalModules = allModules.length;
  const completedModules = allModules.filter((m: DisplayModule) => m.progress === 100).length;
  const overallProgress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learn</h1>
          <p className="mt-1 text-gray-500">
            Master techniques to improve your delivery and persuasion skills
          </p>
        </div>
        <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border">
          <span className="text-sm text-gray-500">Overall Progress</span>
          <div className="w-24 h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-900">{overallProgress}%</span>
        </div>
      </div>

      {/* Category tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Learning categories">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={clsx(
                'pb-3 text-sm font-medium border-b-2 transition-colors',
                activeCategory === category.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {category.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Module grid */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">Loading modules...</div>
      )}
      {!isLoading && isError && (
        <LoadErrorNotice
          error={error}
          onRetry={() => void refetch()}
          failedMessage="The learning modules could not be loaded."
          testIdPrefix="learn-modules"
        />
      )}
      {!isLoading && !isError && modules.length === 0 && (
        <div className="text-center py-12" data-testid="learn-modules-empty">
          <p className="text-gray-500">No learning modules are available yet.</p>
          <Link to="/practice" className="mt-2 inline-block text-sm text-primary-600 hover:underline">
            Practice in the meantime
          </Link>
        </div>
      )}
      {!isLoading && !isError && modules.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module) => (
            <ModuleCard key={module.id} module={module} category={activeCategory} />
          ))}
        </div>
      )}

      {/* Daily practice streak (a nudge to /practice; not a separate challenge system) */}
      <div className="card bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FlameIcon className="w-5 h-5" />
                <span className="text-sm font-medium text-primary-100">Daily Practice</span>
              </div>
              <h3 className="text-lg font-semibold">Practice today</h3>
              <p className="text-primary-100 mt-1">{streakMessage(currentStreak)}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {showTeamChallenges && (
                <Link
                  to="/challenges"
                  data-testid="learn-team-challenges"
                  className="border border-white/70 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors text-center"
                >
                  Team Challenges
                </Link>
              )}
              <Link
                to="/practice"
                className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors text-center"
              >
                Start Practicing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick tips section */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Quick Tips</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TipCard
              icon={<MicIcon className="w-5 h-5" />}
              title="Warm Up Daily"
              description="Spend 5 minutes each morning on vocal exercises."
            />
            <TipCard
              icon={<ClockIcon className="w-5 h-5" />}
              title="Pace Yourself"
              description="Aim for 150 words per minute for optimal comprehension."
            />
            <TipCard
              icon={<SparklesIcon className="w-5 h-5" />}
              title="Practice in Context"
              description="Rehearse in conditions similar to your actual presentation."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ModuleCardProps {
  module: DisplayModule;
  category: string;
}

function ModuleCard({ module, category }: ModuleCardProps) {
  const notAvailable = module.availableLessons === 0;
  const isComplete = !notAvailable && module.progress === 100;
  const isStarted = module.progress > 0 && module.progress < 100;

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="card-body">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{module.title}</h3>
          {isComplete && (
            <span className="text-green-500">
              <CheckCircleIcon className="w-5 h-5" />
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">{module.description}</p>

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <span className="flex items-center gap-1">
            <BookIcon className="w-4 h-4" />
            {module.availableLessons === module.lessons
              ? `${module.lessons} lessons`
              : `${module.availableLessons} of ${module.lessons} lessons available`}
          </span>
          <span className="flex items-center gap-1">
            <ClockIcon className="w-4 h-4" />
            {module.duration}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Progress</span>
            <span className={clsx('font-medium', isComplete ? 'text-green-600' : 'text-gray-700')}>
              {module.progress}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                isComplete ? 'bg-green-500' : 'bg-primary-500'
              )}
              style={{ width: `${module.progress}%` }}
            />
          </div>
        </div>

        <Link
          to={`/learn/${category}/${module.slug}`}
          className={clsx(
            'block w-full py-2 text-center rounded-lg font-medium transition-colors',
            isComplete
              ? 'bg-green-50 text-green-700 hover:bg-green-100'
              : isStarted
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          )}
        >
          {notAvailable ? 'Not available yet' : isComplete ? 'Review' : isStarted ? 'Continue' : 'Start'}
        </Link>
      </div>
    </div>
  );
}

interface TipCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function TipCard({ icon, title, description }: TipCardProps) {
  return (
    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0 w-10 h-10 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-gray-900 text-sm">{title}</h4>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

// Icon components
function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}
